import { Paper, Table, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import styled from 'styled-components';
import type { EmailTemplateSummaryRes } from '@dealstack/shared/lib/generated';

import { useTemplateSummaryLabels } from '../hooks/useTemplateSummaryLabels';
import { TemplateStatus } from './TemplateStatus';

interface Props {
	templates: EmailTemplateSummaryRes[];
	togglingType: string | null;
	readOnly?: boolean;
	onSelect: (templateType: string) => void;
	onToggle: (templateType: string, enabled: boolean) => void;
}

const TemplateRows = styled.div`
	/* Fixed layout so the nowrap cells below truncate instead of widening the
	   table past its container. */
	table {
		table-layout: fixed;
		width: 100%;
	}

	thead th {
		height: 3rem;
		padding: 0 var(--mantine-spacing-lg);
		/* Mantine leaves cells on the baseline, which lifts a one-line cell. */
		vertical-align: middle;
		font-size: 0.875rem;
		font-weight: 600;
		color: var(--mantine-color-text);
	}

	tbody tr {
		cursor: pointer;
	}

	tbody tr:hover {
		background-color: var(--mantine-color-gray-0);
	}

	tbody td {
		height: 3rem;
		padding: 0 var(--mantine-spacing-lg);
		vertical-align: middle;
	}

	/* Text is pre-line app-wide, which beats Mantine's own truncate. */
	tbody td p {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`;

const CellStack = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`;

export const EmailTemplateTable = ({
	templates,
	togglingType,
	readOnly,
	onSelect,
	onToggle,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { versionLabel, statusLabel } = useTemplateSummaryLabels();

	return (
		<Paper withBorder radius="md" style={{ borderColor: 'var(--mantine-color-gray-4)' }}>
			<TemplateRows>
				<Table verticalSpacing={0} horizontalSpacing="lg">
					<Table.Thead>
						<Table.Tr>
							<Table.Th>
								{tMipManage('email_templates.list.columns.name', {
									defaultValue: 'Template name',
								})}
							</Table.Th>
							<Table.Th w={220}>
								{tMipManage('email_templates.list.columns.version', {
									defaultValue: 'Version',
								})}
							</Table.Th>
							<Table.Th w={180}>
								{tMipManage('email_templates.list.columns.status')}
							</Table.Th>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{templates.map((template) => {
							const version = versionLabel(template);
							return (
								<Table.Tr
									key={template.templateType}
									onClick={() => onSelect(template.templateType)}
								>
									<Table.Td>
										<CellStack>
											<Text size="md" fw={500} lh="1.3125rem" truncate>
												{template.name}
											</Text>
											<Text
												fz="0.75rem"
												c="gray.9"
												lh="1.125rem"
												truncate
												title={template.description}
											>
												{template.description}
											</Text>
										</CellStack>
									</Table.Td>
									<Table.Td>
										<Text fz="0.75rem" c="gray.9" lh="1.125rem" truncate>
											{version.editor
												? `${version.label} · ${version.editor}`
												: version.label}
										</Text>
									</Table.Td>
									<Table.Td>
										<TemplateStatus
											label={statusLabel(template)}
											active={template.enabled}
											disabled={
												togglingType === template.templateType || readOnly
											}
											onToggle={(enabled) =>
												onToggle(template.templateType, enabled)
											}
										/>
									</Table.Td>
								</Table.Tr>
							);
						})}
					</Table.Tbody>
				</Table>
			</TemplateRows>
		</Paper>
	);
};
